import { EmptyState } from "@/components/ui/feedback";
import { Badge } from "@/components/ui/badge";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import {
  RecoveryWipHeader,
  RecoveryWipTabs,
  WipActionDescription,
} from "@/features/recovery/recovery-wip";

/**
 * RCV-04 — Recovery Execution Monitor, ported from
 * `.design-ref/prototype/features/recovery/recovery-execution.tsx` (ALL-5825).
 *
 * No recovery backend exists in this environment, and this is a
 * custody-adjacent ceremony, so the port keeps the designer's structure — the
 * breadcrumb, the header with its state switcher, the status card with its
 * badge / fingerprint / progress track / step list, and the execution-log
 * console — and fills none of it: every value is the shared "not available
 * yet" marker, the step list is a designed empty state, the progress track
 * carries no fill (nothing is 0%, there is simply no execution to measure),
 * and the state switcher is inert because no execution record exists to switch
 * between. Nothing here is estimated or defaulted.
 */
const TABS = ["In Progress", "Complete", "Failed"];

export default function RecoveryExecutionPage() {
  return (
    <div>
      <RecoveryWipHeader
        title="Recovery Execution"
        description="Executing and tracking progress of a recovery ceremony. This area has no backend in this environment yet."
        actions={<RecoveryWipTabs label="Recovery execution view" tabs={TABS} active="In Progress" />}
      />
      <WipActionDescription />

      <div className="mb-5 rounded-[10px] border border-line bg-surface p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <Badge variant="neutral">
            <UnavailableValue />
          </Badge>
          <span className="font-mono text-xs text-muted">
            <UnavailableValue />
          </span>
        </div>

        {/* Progress of a ceremony that does not exist is not 0% — the track is
            drawn empty rather than filled to a value nobody measured. */}
        <div aria-hidden className="mb-5 h-2 overflow-hidden rounded-full bg-bg" />

        <EmptyState
          className="py-6"
          title="No recovery execution to show"
          description="No recovery ceremony is being executed or has recently completed, so there are no steps to track."
          meta="Steps and their state come from the recovery backend. Nothing on this page is estimated."
        />
      </div>

      <div className="overflow-hidden rounded-[10px] border border-line bg-console-bg">
        <div className="border-b border-line bg-console-panel px-4 py-2 font-mono text-xs text-console-muted">
          Execution Log
        </div>
        <div className="p-4 font-mono text-[13px] leading-relaxed text-console-muted">
          No execution log entries.
        </div>
      </div>
    </div>
  );
}
